import React from 'react';

const SIGNS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
               "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

const CONTRIBUTORS = ["Saturn", "Jupiter", "Mars", "Sun", "Venus", "Mercury", "Moon", "Lagna"];

/**
 * Component to display Ashtakavarga in house-wise format (Bhava-wise)
 * Matches the display format from Parashara's Light
 */
const AshtakavargaHouseWise = ({ ashtakavargaData, lagnaSign }) => {
  if (!ashtakavargaData || !lagnaSign) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
        <p className="text-yellow-800">Ashtakavarga data not available</p>
      </div>
    );
  }

  // Get Lagna sign index
  const lagnaIndex = SIGNS.indexOf(lagnaSign);
  if (lagnaIndex === -1) {
    return <div className="text-red-600">Invalid Lagna sign: {lagnaSign}</div>;
  }

  // Get detailed BAV matrices
  const detailedBAVs = ashtakavargaData['13.5_detailed_bav_matrices'] || {};
  const savMatrix = ashtakavargaData['13.6_sav_detailed_matrix'] || {};
  
  // Planets to display
  const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];

  /**
   * Convert sign-wise matrix to house-wise matrix
   */
  const convertToHouseWise = (bavData) => {
    if (!bavData || !bavData.matrix) return null;

    const houseWiseMatrix = [];
    const houseWiseColumnTotals = [];

    // Rotate columns to show houses 1-12 instead of signs Aries-Pisces
    for (let contributor_idx = 0; contributor_idx < CONTRIBUTORS.length; contributor_idx++) {
      const newRow = [];
      for (let house = 0; house < 12; house++) {
        const signIndex = (lagnaIndex + house) % 12;
        newRow.push(bavData.matrix[contributor_idx][signIndex]);
      }
      houseWiseMatrix.push(newRow);
    }

    // Rotate column totals
    for (let house = 0; house < 12; house++) {
      const signIndex = (lagnaIndex + house) % 12;
      houseWiseColumnTotals.push(bavData.column_totals[signIndex]);
    }

    return {
      matrix: houseWiseMatrix,
      row_totals: bavData.row_totals,
      column_totals: houseWiseColumnTotals,
      grand_total: bavData.grand_total
    };
  };

  /**
   * Render a single Bhinnashtakavarga table
   */
  const renderBAVTable = (planetName, bavData) => {
    const houseWiseData = convertToHouseWise(bavData);
    if (!houseWiseData) return null;

    // Get house signs
    const houseSigns = [];
    for (let h = 0; h < 12; h++) {
      houseSigns.push(SIGNS[(lagnaIndex + h) % 12]);
    }

    return (
      <div key={planetName} className="mb-8 bg-white rounded-lg shadow-md p-4">
        <h3 className="text-xl font-bold mb-3 text-purple-700">{planetName}</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-purple-100">
                <th className="border border-purple-300 px-2 py-1 text-left">Sign</th>
                {houseSigns.map((sign, idx) => (
                  <th key={idx} className="border border-purple-300 px-2 py-1 text-center">
                    {idx + 1}
                  </th>
                ))}
                <th className="border border-purple-300 px-2 py-1 text-center bg-purple-200">
                  Totals
                </th>
              </tr>
              <tr className="bg-purple-50">
                <th className="border border-purple-300 px-2 py-1 text-left"></th>
                {houseSigns.map((sign, idx) => (
                  <th key={idx} className="border border-purple-300 px-1 py-1 text-xs text-center">
                    {sign.substring(0, 3)}
                  </th>
                ))}
                <th className="border border-purple-300 px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {CONTRIBUTORS.map((contributor, rowIdx) => (
                <tr key={contributor} className={rowIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="border border-gray-300 px-2 py-1 font-medium">
                    {contributor}
                  </td>
                  {houseWiseData.matrix[rowIdx].map((bindu, colIdx) => (
                    <td
                      key={colIdx}
                      className={`border border-gray-300 px-2 py-1 text-center ${
                        bindu === 1 ? 'bg-green-100 font-bold' : 'bg-red-50 text-gray-400'
                      }`}
                    >
                      {bindu}
                    </td>
                  ))}
                  <td className="border border-gray-300 px-2 py-1 text-center font-bold bg-yellow-100">
                    {houseWiseData.row_totals[rowIdx]}
                  </td>
                </tr>
              ))}
              <tr className="bg-yellow-100 font-bold">
                <td className="border border-gray-300 px-2 py-1">Totals</td>
                {houseWiseData.column_totals.map((total, idx) => (
                  <td key={idx} className="border border-gray-300 px-2 py-1 text-center">
                    {total}
                  </td>
                ))}
                <td className="border border-gray-300 px-2 py-1 text-center bg-yellow-200">
                  {houseWiseData.grand_total}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  /**
   * Render Sarvashtakavarga summary
   */
  const renderSAVSummary = () => {
    if (!savMatrix || !savMatrix.column_totals) return null;

    // Rotate SAV column totals to house-wise
    const houseWiseSAV = [];
    const houseSigns = [];
    for (let h = 0; h < 12; h++) {
      const signIndex = (lagnaIndex + h) % 12;
      houseWiseSAV.push(savMatrix.column_totals[signIndex]);
      houseSigns.push(SIGNS[signIndex]);
    }

    return (
      <div className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg shadow-lg p-6">
        <h3 className="text-2xl font-bold mb-4 text-purple-800">
          Sarvashtakavarga (SAV) Summary
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Total bindus across all 7 planets for each house
        </p>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-purple-200 to-blue-200">
                <th className="border border-purple-300 px-3 py-2 text-center">House</th>
                {Array.from({ length: 12 }, (_, i) => (
                  <th key={i} className="border border-purple-300 px-3 py-2 text-center">
                    {i + 1}
                  </th>
                ))}
                <th className="border border-purple-300 px-3 py-2 text-center bg-purple-300">
                  Total
                </th>
              </tr>
              <tr className="bg-purple-100">
                <th className="border border-purple-300 px-3 py-2 text-center">Sign</th>
                {houseSigns.map((sign, idx) => (
                  <th key={idx} className="border border-purple-300 px-2 py-2 text-sm text-center">
                    {sign.substring(0, 3)}
                  </th>
                ))}
                <th className="border border-purple-300 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white">
                <td className="border border-gray-300 px-3 py-2 font-bold text-center">
                  Bindus
                </td>
                {houseWiseSAV.map((total, idx) => (
                  <td
                    key={idx}
                    className={`border border-gray-300 px-3 py-2 text-center font-bold ${
                      total >= 30 ? 'bg-green-100' : total >= 25 ? 'bg-yellow-100' : 'bg-red-100'
                    }`}
                  >
                    {total}
                  </td>
                ))}
                <td className="border border-gray-300 px-3 py-2 text-center font-bold text-lg bg-gradient-to-r from-yellow-200 to-orange-200">
                  {savMatrix.grand_total}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          <p className="mb-2">
            <span className="font-semibold">Classical Total:</span> {savMatrix.grand_total} bindus
            {savMatrix.grand_total === 337 && (
              <span className="ml-2 text-green-600 font-semibold">✓ Verified</span>
            )}
          </p>
          <p className="text-xs italic">
            Color coding: Green (≥30 bindus - Strong), Yellow (25-29 - Moderate), Red (&lt;25 - Weak)
          </p>
        </div>
      </div>
    );
  };

  /**
   * Render Lagna Ashtakavarga
   */
  const renderLagnaBAV = () => {
    const lagnaBAV = ashtakavargaData['13.3_lagna_ashtakavarga'];
    if (!lagnaBAV || !lagnaBAV.per_sign) return null;

    // Rotate to house-wise
    const houseWiseBindus = [];
    const houseSigns = [];
    for (let h = 0; h < 12; h++) {
      const signIndex = (lagnaIndex + h) % 12;
      const signData = lagnaBAV.per_sign[signIndex];
      // Extract bindus value from object or use directly if it's a number
      const bindus = typeof signData === 'object' ? signData.bindus : signData;
      houseWiseBindus.push(bindus);
      houseSigns.push(SIGNS[signIndex]);
    }

    return (
      <div className="mb-8 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg shadow-md p-4">
        <h3 className="text-xl font-bold mb-3 text-cyan-700">Lagna Ashtakavarga</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-cyan-100">
                <th className="border border-cyan-300 px-2 py-1 text-center">House</th>
                {Array.from({ length: 12 }, (_, i) => (
                  <th key={i} className="border border-cyan-300 px-2 py-1 text-center">
                    {i + 1}
                  </th>
                ))}
                <th className="border border-cyan-300 px-2 py-1 text-center bg-cyan-200">
                  Total
                </th>
              </tr>
              <tr className="bg-cyan-50">
                <th className="border border-cyan-300 px-2 py-1 text-center">Sign</th>
                {houseSigns.map((sign, idx) => (
                  <th key={idx} className="border border-cyan-300 px-1 py-1 text-xs text-center">
                    {sign.substring(0, 3)}
                  </th>
                ))}
                <th className="border border-cyan-300 px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white">
                <td className="border border-gray-300 px-2 py-1 font-medium text-center">
                  Bindus
                </td>
                {houseWiseBindus.map((bindu, idx) => (
                  <td
                    key={idx}
                    className={`border border-gray-300 px-2 py-1 text-center font-bold ${
                      bindu >= 4 ? 'bg-green-100' : bindu >= 3 ? 'bg-yellow-100' : 'bg-red-100'
                    }`}
                  >
                    {bindu}
                  </td>
                ))}
                <td className="border border-gray-300 px-2 py-1 text-center font-bold bg-yellow-100">
                  {lagnaBAV.total}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg p-6 shadow-lg">
        <h2 className="text-3xl font-bold mb-2">Ashtakavarga System - Bhinnashtakavarga</h2>
        <p className="text-purple-100">
          House-wise (Bhava) Ashtakavarga for Lagna in <span className="font-bold">{lagnaSign}</span>
        </p>
        <p className="text-sm text-purple-200 mt-2">
          Classical Parasara system with 8 contributors × 12 houses showing bindu (benefic point) distribution
        </p>
      </div>

      {/* SAV Summary */}
      {renderSAVSummary()}

      {/* All Planet BAV Tables */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-purple-300 pb-2">
          Individual Planet Bhinnashtakavarga Tables
        </h3>
        {planets.map(planet => {
          const bavData = detailedBAVs[planet];
          if (!bavData) return null;
          return renderBAVTable(planet, bavData);
        })}
      </div>

      {/* Lagna Ashtakavarga */}
      {renderLagnaBAV()}

      {/* Footer Note */}
      <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-700">
        <h4 className="font-semibold mb-2">Reading the Tables:</h4>
        <ul className="list-disc list-inside space-y-1">
          <li><span className="font-semibold">Rows:</span> 8 contributors (7 planets + Lagna) giving bindus</li>
          <li><span className="font-semibold">Columns:</span> 12 houses (starting from your Lagna in {lagnaSign})</li>
          <li><span className="font-semibold">1 (green):</span> Benefic point present</li>
          <li><span className="font-semibold">0 (gray):</span> No benefic point</li>
          <li><span className="font-semibold">Totals:</span> Sum of bindus for each contributor (row) and house (column)</li>
        </ul>
      </div>
    </div>
  );
};

export default AshtakavargaHouseWise;
